import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { DebounceUtility } from '@ebw-monorepo/utils';
import { lastValueFrom } from 'rxjs';
import { PsaService } from '../../../../services/psa.service';
import { SnackbarService } from '../../../../services/snackbar.service';

/**
 * Una sezione del form per una singola tipologia associata all'evento: tipologia + gravita' +
 * eventuale "assegna a tutti" + elenco elementi di rete associati a QUELLA tipologia.
 * Ogni riga ha la propria ricerca con debounce indipendente dalle altre.
 */
interface RigaTipologia {
  id?: string;
  tipologia: string;
  gravita: number | null;
  assignAll: boolean;
  inputValue: string | null;
  filterValue: string | null;
  listEntities: any[];
  associatedEntities: any[];
  associatedEntitiesFiltered: any[];
  debounce: DebounceUtility;
  // "assegna a tutti": numero di elementi della tipologia (gli elementi aggiunti dalla spunta hanno daAssegnaATutti)
  totaleTipologia: number | null;
  caricamentoTutti: boolean;
}

@Component({
  selector: 'ebw-monorepo-detail-event',
  templateUrl: './detail-event.component.html',
  styleUrls: ['./detail-event.component.scss']
})
export class DetailEventComponent implements OnInit, OnDestroy {
  riskEventForm: FormGroup;
  righe: RigaTipologia[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any = {},
    private _formBuilder: FormBuilder,
    private _psaService: PsaService,
    private _dialogRef: MatDialogRef<DetailEventComponent>,
    private _ebwModalService: EbwModalService,
    private _snackbarService: SnackbarService,
    private _i18NService: I18NService
  ) { }

  async ngOnInit() {
    this.riskEventForm = this._formBuilder.group({
      nome: ['', Validators.required],
      descrizione: ['', Validators.required],
      pericolo_chimico: false,
      pericolo_fisico: false,
      pericolo_biologico: false,
      pericolo_quantitativo: false
    });

    this._psaService._getEntityTypes().subscribe((resp) => {
      this.typesList = resp;
    });

    if (this.data?.id) {
      this.riskEventForm.patchValue({
        nome: this.data.nome || '',
        descrizione: this.data.descrizione || '',
        pericolo_chimico: this.data.pericolo_chimico || false,
        pericolo_fisico: this.data.pericolo_fisico || false,
        pericolo_biologico: this.data.pericolo_biologico || false,
        pericolo_quantitativo: this.data.pericolo_quantitativo || false
      });
      this.riskEventForm.get('nome')?.disable();
      this.riskEventForm.get('descrizione')?.disable();

      const event = await lastValueFrom(this._psaService._getEvent({ id: this.data.id }));
      const tipologie = event?.tipologie || [];
      const entities = event?.entities || [];

      this.righe = tipologie.map((t: any) => {
        const riga = this._creaRigaVuota();
        riga.id = t.id;
        riga.tipologia = t.tipologia;
        riga.gravita = t.gravita || null;
        riga.associatedEntities = entities.filter((e: any) => e.schema_idropotabile?.tipologia === t.tipologia);
        riga.associatedEntitiesFiltered = riga.associatedEntities;
        return riga;
      });
    }

    if (!this.righe.length) {
      this.righe = [this._creaRigaVuota()];
    }
  }

  typesList: any[] = [];

  private _creaRigaVuota(): RigaTipologia {
    const riga: RigaTipologia = {
      tipologia: '',
      gravita: null,
      assignAll: false,
      inputValue: null,
      filterValue: null,
      listEntities: [],
      associatedEntities: [],
      associatedEntitiesFiltered: [],
      debounce: new DebounceUtility(300),
      totaleTipologia: null,
      caricamentoTutti: false
    };
    riga.debounce.debouncedSubject.subscribe((value) => {
      this._psaService._getListElements(value, riga.tipologia).subscribe((result) => {
        riga.listEntities = result;
      });
    });
    return riga;
  }

  /**
   * Tipologie ancora selezionabili per questa riga: quelle disponibili al netto di quelle gia'
   * scelte nelle altre righe.
   */
  tipologieDisponibili(riga: RigaTipologia): any[] {
    const scelteAltrove = this.righe.filter((r) => r !== riga).map((r) => r.tipologia);
    return this.typesList.filter((t) => t.tipologia === riga.tipologia || !scelteAltrove.includes(t.tipologia));
  }

  /**
   * Risultati della ricerca al netto degli elementi gia' associati alla riga: tolti dal cestino
   * tornano disponibili.
   */
  elementiSelezionabili(riga: RigaTipologia): any[] {
    return riga.listEntities.filter((option) => !riga.associatedEntities.some((e) => +e.schema_idropotabile_id === +option.id));
  }

  // dopo la selezione il campo di ricerca resta vuoto, pronto per la ricerca successiva
  readonly nessunTesto = (): string => '';

  get puoAggiungereRiga(): boolean {
    if (!this.righe.length) return this.typesList.length > 0;
    const ultima = this.righe[this.righe.length - 1];
    return !!ultima?.tipologia && this.righe.length < this.typesList.length;
  }

  aggiungiRiga(): void {
    this.righe.push(this._creaRigaVuota());
  }

  async rimuoviRiga(riga: RigaTipologia): Promise<void> {
    if (riga.id) {
      const conferma = await lastValueFrom(
        this._ebwModalService
          .openConfirmModal({
            title: this._i18NService.getInstantValue('map.sidebar.psa.actions.delete'),
            content: this._i18NService.getInstantValue('map.sidebar.psa.actions.delete_message')
          })
          .afterClosed()
      );
      if (!conferma) {
        return;
      }
    }
    riga.debounce.complete();
    this.righe = this.righe.filter((r) => r !== riga);
    // senza righe la sezione tipologie torna allo stato iniziale: una riga vuota da compilare
    if (!this.righe.length) {
      this.righe = [this._creaRigaVuota()];
    }
  }

  /**
   * Spuntando si aggiungono subito alla lista tutti gli elementi della tipologia (quelli gia'
   * presenti tengono la probabilita'); togliendo la spunta si tolgono gli elementi aggiunti dalla
   * spunta e restano quelli inseriti a mano o gia' salvati.
   */
  async onAssegnaATuttiChange(riga: RigaTipologia, checked: boolean): Promise<void> {
    // la checkbox non deve restare evidenziata dopo il click
    (document.activeElement as HTMLElement)?.blur();

    if (!checked) {
      riga.associatedEntities = riga.associatedEntities.filter((e) => !e.daAssegnaATutti);
      this._annullaAssegnaATutti(riga);
      this.onInputFilter(riga);
      return;
    }

    riga.caricamentoTutti = true;
    try {
      const tutti = await lastValueFrom(this._psaService._getListElements('', riga.tipologia));
      const presenti = new Set(riga.associatedEntities.map((e) => +e.schema_idropotabile_id));
      const nuovi = tutti
        .filter((el: any) => !presenti.has(+el.id))
        .map((el: any) => ({
          schema_idropotabile_id: el.id,
          schema_idropotabile: { search_term: el.search_term, type: el.type },
          probabilita: null,
          daAssegnaATutti: true
        }));
      riga.associatedEntities = [...riga.associatedEntities, ...nuovi];
      riga.totaleTipologia = tutti.length;
    } finally {
      riga.caricamentoTutti = false;
    }
    this.onInputFilter(riga);
  }

  // cambiando tipologia la spunta "assegna a tutti" non vale piu'
  onTipologiaChange(riga: RigaTipologia): void {
    riga.assignAll = false;
    this._annullaAssegnaATutti(riga);
  }

  private _annullaAssegnaATutti(riga: RigaTipologia): void {
    riga.totaleTipologia = null;
  }

  onInput(riga: RigaTipologia): void {
    riga.debounce.trigger(riga.inputValue);
  }

  onInputFilter(riga: RigaTipologia): void {
    if (riga.filterValue) {
      const filterValueLower = riga.filterValue.toLowerCase();
      riga.associatedEntitiesFiltered = riga.associatedEntities.filter((option) => {
        const searchTermMatch = option.schema_idropotabile?.search_term?.toLowerCase().includes(filterValueLower);
        const typeMatch = option.schema_idropotabile?.type?.toLowerCase().includes(filterValueLower);
        return searchTermMatch || typeMatch;
      });
      return;
    }
    riga.associatedEntitiesFiltered = riga.associatedEntities;
  }

  onOptionSelected(riga: RigaTipologia, selectedValue: any): void {
    const isAlreadyPresent = riga.associatedEntities.some((entity) => entity.schema_idropotabile_id === selectedValue.id);
    if (isAlreadyPresent) {
      this._snackbarService.open('Elemento già inserito');
    } else {
      riga.associatedEntities.push({
        schema_idropotabile_id: selectedValue.id,
        schema_idropotabile: {
          search_term: selectedValue.search_term,
          type: selectedValue.type
        }
      });
    }
    riga.inputValue = null;
    riga.associatedEntitiesFiltered = riga.associatedEntities;
  }

  /**
   * Toglie l'elemento solo dalla lista: come le altre modifiche della finestra diventa effettivo
   * con Salva (il backend scollega gli elementi non piu' presenti e, in cascata, le loro azioni
   * mitigatrici) e Annulla lo scarta.
   */
  async removeEntitiy(riga: RigaTipologia, element: any): Promise<void> {
    if (element.id) {
      const response = await lastValueFrom(
        this._ebwModalService
          .openConfirmModal({
            title: this._i18NService.getInstantValue('map.sidebar.psa.actions.delete'),
            content: this._i18NService.getInstantValue('map.sidebar.psa.risk_event.remove_element_message')
          })
          .afterClosed()
      );
      if (!response) {
        return;
      }
    }

    const index = riga.associatedEntities.indexOf(element);
    if (index > -1) {
      riga.associatedEntities.splice(index, 1);
      riga.associatedEntitiesFiltered = riga.associatedEntities;
    }
    // tolto un elemento non sono piu' "tutti": la lista resta quella visibile
    if (riga.assignAll) {
      riga.assignAll = false;
      this._annullaAssegnaATutti(riga);
    }
  }

  onCancel(): void {
    this._dialogRef.close();
  }

  get formValido(): boolean {
    // la gravita' e' obbligatoria per ogni tipologia: parte vuota e l'utente sceglie un valore da 1 a 5
    return this.riskEventForm?.valid && this.righe.length > 0 && this.righe.every((r) => !!r.tipologia && !!r.gravita);
  }

  async onSave(): Promise<void> {
    try {
      // si salva la lista visibile: con "assegna a tutti" contiene gia' tutti gli elementi della tipologia
      const tipologie = this.righe.map((riga) => ({
        id: riga.id,
        tipologia: riga.tipologia,
        gravita: riga.gravita,
        entities: riga.associatedEntities.map((e) => ({
          schema_idropotabile_id: +(e.schema_idropotabile_id || e.id),
          probabilita: e.probabilita || null
        }))
      }));

      const campiComuni = {
        pericolo_chimico: this.riskEventForm.value.pericolo_chimico,
        pericolo_fisico: this.riskEventForm.value.pericolo_fisico,
        pericolo_biologico: this.riskEventForm.value.pericolo_biologico,
        pericolo_quantitativo: this.riskEventForm.value.pericolo_quantitativo
      };

      const response = this.data?.id
        ? await lastValueFrom(this._psaService._updateEvent({ id: this.data.id, ...campiComuni, tipologie }))
        : await lastValueFrom(
          this._psaService._addEvent({
            nome: this.riskEventForm.value.nome,
            descrizione: this.riskEventForm.value.descrizione,
            ...campiComuni,
            tipologie
          })
        );

      this._ebwModalService.openInfoModal({
        title: this._i18NService.getInstantValue('map.sidebar.psa.risk_event.save_title'),
        content: this._i18NService.getInstantValue('common.save_successful')
      });
      this._dialogRef.close(response);
    } catch (error) {
      console.error('[DetailEvent][onSave] ', error);
      const content =
        error?.error?.code === 'EVENT_NAME_DUPLICATE'
          ? this._i18NService.getInstantValue('map.sidebar.psa.risk_event.name_duplicate')
          : error?.error?.message;
      this._ebwModalService.openErrorModal({ title: this._i18NService.getInstantValue('map.sidebar.psa.risk_event.save_title'), content });
    }
  }

  ngOnDestroy(): void {
    this.righe.forEach((riga) => riga.debounce.complete());
  }
}
